import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap'
import { fetchAdminCharities } from '../reducers/charitiesSlice'
import { api } from '../api/charities'
import CharityForm from './CharityForm'

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 3 * 60 * 1000

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function CharityManager() {
  const dispatch = useDispatch()
  const rawCharities = useSelector((state) => state.charities.items ?? [])
  const charities = [...rawCharities].sort((a, b) => a.data.name.localeCompare(b.data.name))
  const loading = useSelector((state) => state.charities.loading)

  const [formMode, setFormMode] = useState(null) // null | 'add' | 'edit'
  const [editingCharity, setEditingCharity] = useState(null)
  const [pendingJob, setPendingJob] = useState(null) // { job_id, url }
  const [pendingDelete, setPendingDelete] = useState(null) // charity object | null
  const [enrichError, setEnrichError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const cancelledRef = useRef(false)
  const successTimeoutRef = useRef(null)

  useEffect(() => {
    if (!successMessage) return
    clearTimeout(successTimeoutRef.current)
    successTimeoutRef.current = setTimeout(() => setSuccessMessage(null), 3000)
    return () => clearTimeout(successTimeoutRef.current)
  }, [successMessage])

  useEffect(() => {
    dispatch(fetchAdminCharities())
    return () => {
      cancelledRef.current = true
    }
  }, [dispatch])

  async function pollJob(job_id) {
    const deadline = Date.now() + POLL_TIMEOUT_MS
    try {
      while (Date.now() < deadline) {
        if (cancelledRef.current) return
        await sleep(POLL_INTERVAL_MS)
        if (cancelledRef.current) return
        const job = await api.getJob(job_id)
        if (job.status === 'complete') {
          setPendingJob(null)
          setSuccessMessage('Charity added')
          dispatch(fetchAdminCharities())
          return
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Enrichment failed.')
        }
      }
      throw new Error('Enrichment timed out — please try again.')
    } catch (err) {
      setPendingJob(null)
      setEnrichError(err.message)
    }
  }

  async function handleAdd(url) {
    setEnrichError(null)
    cancelledRef.current = false
    const { job_id } = await api.postCharity(url)
    setFormMode(null)
    setPendingJob({ job_id, url })
    pollJob(job_id)
  }

  async function handleEditSubmit(data) {
    await api.putCharity(editingCharity.id, data)
    setFormMode(null)
    setEditingCharity(null)
    setSuccessMessage('Charity updated')
    dispatch(fetchAdminCharities())
  }

  async function handleDelete(id) {
    await api.deleteCharity(id)
    setPendingDelete(null)
    dispatch(fetchAdminCharities())
  }

  function handleCancel() {
    cancelledRef.current = true
    setEnrichError(null)
    setFormMode(null)
    setEditingCharity(null)
  }

  if (formMode === 'add') {
    return (
      <>
        <h3 className="fs-5 fw-semibold mb-3">Add Charity</h3>
        <CharityForm mode="add" onSubmit={handleAdd} onCancel={handleCancel} />
      </>
    )
  }

  if (formMode === 'edit') {
    return (
      <>
        <h3 className="fs-5 fw-semibold mb-3">Edit Charity</h3>
        <CharityForm
          mode="edit"
          charity={editingCharity}
          onSubmit={handleEditSubmit}
          onCancel={handleCancel}
        />
      </>
    )
  }

  return (
    <>
      <div className="mb-3 d-flex justify-content-end">
        <Button
          variant="primary"
          size="sm"
          disabled={!!pendingJob}
          onClick={() => {
            setEnrichError(null)
            setFormMode('add')
          }}
        >
          Add Charity
        </Button>
      </div>
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
          {successMessage}
        </Alert>
      )}
      {enrichError && (
        <Alert variant="danger" onClose={() => setEnrichError(null)} dismissible>
          {enrichError}
        </Alert>
      )}
      {loading && (
        <div className="d-flex align-items-center gap-2 mb-2 text-muted small">
          <Spinner animation="border" size="sm" aria-label="Loading" />
          <span>Refreshing…</span>
        </div>
      )}
      <Table responsive hover>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Category</th>
            <th scope="col">Active</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {pendingJob && (
            <tr>
              <td colSpan={4} className="text-muted fst-italic">
                <Spinner animation="border" size="sm" aria-hidden="true" className="me-2" />
                Adding {pendingJob.url} — AI enrichment in progress…
              </td>
            </tr>
          )}
          {charities.map((charity) => (
            <tr key={charity.id}>
              <td>{charity.data.name}</td>
              <td>{charity.data.category}</td>
              <td>{charity.active ? 'Yes' : 'No'}</td>
              <td>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="me-2"
                  onClick={() => {
                    setEditingCharity(charity)
                    setFormMode('edit')
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setPendingDelete(charity)}
                >
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Modal show={!!pendingDelete} onHide={() => setPendingDelete(null)} centered animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Delete charity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{' '}
          <strong>{pendingDelete?.data.name}</strong>? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPendingDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(pendingDelete.id)}>
            Confirm delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default CharityManager
